import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ShoppingCart, Minus, Plus, ChevronLeft, Package, Truck, Shield, Check, Zap } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/store/products/${productId}`);
      setProduct(response.data);
      
      // Fetch related products from same category
      if (response.data.category_id) {
        const relatedResponse = await axios.get(`${API_URL}/store/products?category_id=${response.data.category_id}&limit=4`);
        setRelatedProducts(relatedResponse.data.products.filter(p => p.id !== response.data.id).slice(0, 4));
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
      toast.success(`${quantity}x ${product.name} added to cart!`);
    }
  };

  const handleBuyNow = () => {
    if (product) {
      addToCart(product, quantity);
      navigate('/cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-24 bg-dark">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-white/10 rounded w-32 mb-8" />
            <div className="grid md:grid-cols-2 gap-12">
              <div className="h-96 bg-white/5 rounded-xl" />
              <div className="space-y-4">
                <div className="h-8 bg-white/10 rounded w-3/4" />
                <div className="h-4 bg-white/5 rounded w-1/2" />
                <div className="h-12 bg-white/10 rounded w-1/3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-28 pb-24 bg-dark flex items-center justify-center">
        <div className="text-center">
          <Package size={64} className="mx-auto text-gray-600 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Product Not Found</h2>
          <Link to="/store" className="text-primary hover:underline">Back to Store</Link>
        </div>
      </div>
    );
  }

  const currentPrice = product.discount_price || product.price;
  const savings = product.discount_price ? product.price - product.discount_price : 0;

  return (
    <div className="min-h-screen pt-28 pb-24 bg-dark">
      <SEO 
        title={`${product.name} | UAV Geo Solutions Store`}
        description={product.short_description}
        image={product.images?.[0]}
        url={`/store/product/${product.slug || product.id}`}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Breadcrumb */}
        <Link to="/store" className="inline-flex items-center gap-2 text-gray-400 hover:text-primary mb-8 transition-colors">
          <ChevronLeft size={20} />
          Back to Store
        </Link>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Product Images */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {/* Main Image */}
            <div className="glass rounded-xl overflow-hidden mb-4">
              <div className="aspect-square bg-gradient-to-br from-white/5 to-white/0">
                {product.images?.[selectedImage] ? (
                  <img
                    src={product.images[selectedImage]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-600">
                    <Package size={96} />
                  </div>
                )}
              </div>
            </div>

            {/* Thumbnail Gallery */}
            {product.images?.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === index ? 'border-primary' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {/* SKU */}
            <p className="text-sm text-gray-500 mb-2">SKU: {product.sku}</p>

            {/* Name */}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">{product.name}</h1>

            {/* Short Description */}
            <p className="text-gray-400 mb-6">{product.short_description}</p>

            {/* Price */}
            <div className="mb-6">
              {product.discount_price ? (
                <div className="flex items-center gap-4">
                  <span className="text-4xl font-bold text-primary">₹{product.discount_price.toLocaleString()}</span>
                  <span className="text-xl text-gray-500 line-through">₹{product.price.toLocaleString()}</span>
                  <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-semibold">
                    Save ₹{savings.toLocaleString()}
                  </span>
                </div>
              ) : (
                <span className="text-4xl font-bold text-primary">₹{product.price.toLocaleString()}</span>
              )}
            </div>

            {/* Stock Status */}
            <div className="flex items-center gap-2 mb-6">
              {product.stock_quantity > 0 ? (
                <>
                  <Check className="text-green-400" size={20} />
                  <span className="text-green-400">In Stock ({product.stock_quantity} available)</span>
                </>
              ) : (
                <span className="text-red-400">Out of Stock</span>
              )}
            </div>

            {/* Quantity & Action Buttons */}
            <div className="flex flex-col gap-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="flex items-center glass rounded-lg">
                  <button
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="p-3 hover:bg-white/10 transition-colors rounded-l-lg"
                  >
                    <Minus size={20} />
                  </button>
                  <span className="px-6 py-3 font-semibold min-w-[60px] text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(q => Math.min(product.stock_quantity, q + 1))}
                    className="p-3 hover:bg-white/10 transition-colors rounded-r-lg"
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleAddToCart}
                  disabled={product.stock_quantity === 0}
                  className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-white/20"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart size={22} />
                  Add to Cart
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={product.stock_quantity === 0}
                  className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-primary text-dark font-bold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="buy-now-btn"
                >
                  <Zap size={22} />
                  Buy Now
                </button>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="glass rounded-lg p-4 text-center">
                <Truck className="mx-auto mb-2 text-primary" size={24} />
                <p className="text-sm text-gray-400">Fast Delivery</p>
              </div>
              <div className="glass rounded-lg p-4 text-center">
                <Shield className="mx-auto mb-2 text-primary" size={24} />
                <p className="text-sm text-gray-400">Warranty</p>
              </div>
              <div className="glass rounded-lg p-4 text-center">
                <Package className="mx-auto mb-2 text-primary" size={24} />
                <p className="text-sm text-gray-400">Secure Packaging</p>
              </div>
            </div>

            {/* Specifications */}
            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div className="glass rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Specifications</h3>
                <div className="space-y-3">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between py-2 border-b border-white/10 last:border-0">
                      <span className="text-gray-400">{key}</span>
                      <span className="text-white font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Full Description */}
        {product.full_description && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-12 glass rounded-xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-4">Description</h2>
            <div className="prose prose-invert max-w-none">
              <p className="text-gray-300 whitespace-pre-wrap">{product.full_description}</p>
            </div>
          </motion.div>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-16"
          >
            <h2 className="text-2xl font-bold text-white mb-8">Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/store/product/${relatedProduct.slug || relatedProduct.id}`}
                  className="glass rounded-xl overflow-hidden group hover:border-primary/50 transition-all"
                >
                  <div className="h-40 bg-gradient-to-br from-white/5 to-white/0 overflow-hidden">
                    {relatedProduct.images?.[0] ? (
                      <img
                        src={relatedProduct.images[0]}
                        alt={relatedProduct.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-600">
                        <Package size={32} />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-white mb-2 line-clamp-1 group-hover:text-primary transition-colors">
                      {relatedProduct.name}
                    </h3>
                    <p className="text-primary font-bold">
                      ₹{(relatedProduct.discount_price || relatedProduct.price).toLocaleString()}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
